import { useContainerWidth } from 'hooks/general/useContainerWidth';
import useDecompositionGraphData from './hooks/useDecompositionGraphData';
import { calculateContainerHeight, removeFunctionFromProps } from 'utils/helperMethods';
import Loader from 'components/molecules/Loader';
import { useGrowthMarginDriversContext } from './context/GrowthMarginDriversContext';
import SubFilterDropdown from 'components/molecules/GrowthAnalysis/SubFilterDropdown';
import { FilterContextData } from 'contexts/FilterContext';
import { memo, useContext, useEffect, useMemo, useRef } from 'react';
import { isEqual, uniqBy } from 'lodash';
import { nullAttrTypeOption } from '../GrowthAnalysis';
import {
    checkIfNullAttributePresent,
    formatHierarchicalTreeData,
    getLeafNodesAtAttributeType,
    trimBelowDepth,
} from './data/hierarchicalTreeData';
import RightArrowAlt from 'assets/svg/RightArrowAlt';
import { colorConstants } from 'config/colorConstants';
import HierarchicalTreeChart from './HierarchicalTreeChart';
import DownloadExcel from 'components/molecules/MicroIntractions/DownloadExcel';
import CopyToClipBoard from 'components/molecules/MicroIntractions/CopyTOClipBoard';

const DecompositionFilterIcon = () => {
    return (
        <div>
            <RightArrowAlt stroke={colorConstants.violet} />
        </div>
    );
};

const DecompositionGraph = ({ decompositionGraphRef }: { decompositionGraphRef: React.RefObject<HTMLDivElement> }) => {
    const {
        decompositionFilterSelectedItems,
        memoisedDecompositionFilterDropdownOptions,
        setDecompositionGraphData,
        setSelectedNodes,
        setDecompositionFilterSelectedItems,
    } = useGrowthMarginDriversContext();

    const { decompositionGraphData, isDecompositionGraphDataLoading } = useDecompositionGraphData();

    const treeExportData = useMemo(() => {
        return formatHierarchicalTreeData(decompositionGraphData);
    }, [decompositionGraphData, formatHierarchicalTreeData]);

    const { allDimAndAttributeTypeList, timePeriodLoading } = useContext(FilterContextData);

    const chartHeight = calculateContainerHeight(2);
    const chartWidth = useContainerWidth({ containerId: 'decomposition-container' });

    const onDecompositionFilterOptionsChange = (val: any, item: any, index: number, filter?: any) => {
        const DECOMPOSITION_LIMIT = 5;
        let temp = [...decompositionFilterSelectedItems];
        if (!item && !val) {
            // if (decompositionFilterSelectedItems.length >= DECOMPOSITION_LIMIT) {
            //     temp.splice(index, 1, nullAttrTypeOption);
            // } else {
            //     temp.splice(index, 1);
            // }
            if (index < temp.length - 1) {
                temp = temp.slice(0, index);
            } else {
                temp.splice(index, 1, nullAttrTypeOption);
            }
            temp.splice(index, 1);
            const isNullPresent = checkIfNullAttributePresent(temp);
            if (!isNullPresent) temp.push(nullAttrTypeOption);
            setDecompositionGraphData(null);
            const trimmedGraphData = trimBelowDepth(decompositionGraphData, index - 1);
            setDecompositionGraphData(trimmedGraphData);
        } else {
            if (decompositionFilterSelectedItems.length >= DECOMPOSITION_LIMIT) {
                if (index < temp.length - 1) {
                    temp.splice(index, 0, item);
                    temp.splice(temp.length - 1, 1);
                    temp = temp.slice(0, index + 1);
                    temp.push(nullAttrTypeOption);
                } else {
                    temp.splice(index, 0, item);
                    temp.splice(temp.length - 1, 1);
                }
            } else {
                if (index < temp.length - 1) {
                    temp.splice(index, 0, item);
                    temp = temp.slice(0, index + 1);
                    const isNullPresent = checkIfNullAttributePresent(temp);
                    if (!isNullPresent) temp.push(nullAttrTypeOption);
                } else {
                    temp.splice(index, 0, item);
                }
            }
            const parentAttributeLevel = temp[index - 1];
            const nextAttributeLevel = temp[index];
            const leafNodesAtParentLevel = getLeafNodesAtAttributeType(
                decompositionGraphData,
                parentAttributeLevel.key,
            );
            const updatedLeafNodesAtParentLevel = leafNodesAtParentLevel.map((_leafNode) => {
                return {
                    ..._leafNode,
                    nextAttributeType: nextAttributeLevel?.key,
                    nextDimension: nextAttributeLevel?.dimension,
                    nextIsAttribute: nextAttributeLevel?.isAttribute,
                };
            });
            setSelectedNodes(uniqBy(updatedLeafNodesAtParentLevel, 'id'));
        }
        setDecompositionFilterSelectedItems(temp);
    };
    return (
        <>
            <div className="flex items-center justify-between mb-2">
                <p className="text-lg font-semibold">{`Decomposition`}</p>
                {decompositionGraphData && (
                    <div className="flex justify-end gap-2">
                        <CopyToClipBoard refOfComponent={decompositionGraphRef} removeScrollbar />
                        <DownloadExcel
                            title="Decomposition"
                            excelData={treeExportData}
                            headers={['Name', 'Value', 'Attribute Type', 'Percentage']}
                            excludeFilters={true}
                        />
                    </div>
                )}
            </div>
            {isDecompositionGraphDataLoading ? (
                <div className="flex items-center justify-center text-gray-500" style={{ height: chartHeight }}>
                    <Loader />
                </div>
            ) : (
                <>
                    {/* Decomposition Filter Section */}
                    {decompositionGraphData ? (
                        <>
                            <div className="flex gap-2 mb-4">
                                {decompositionFilterSelectedItems.map((item, index) => {
                                    return (
                                        <div
                                            key={index}
                                            className={`flex gap-2 items-center ${
                                                index === 0 && item?.value !== null ? 'pointer-events-none' : ''
                                            }`}
                                        >
                                            <SubFilterDropdown
                                                loading={
                                                    allDimAndAttributeTypeList?.loading || timePeriodLoading || false
                                                }
                                                onChange={onDecompositionFilterOptionsChange}
                                                key={index}
                                                index={index}
                                                options={memoisedDecompositionFilterDropdownOptions}
                                                value={item.label}
                                                from={'growth-analysis'}
                                            />
                                            {decompositionFilterSelectedItems?.length - 1 !== index && (
                                                <DecompositionFilterIcon />
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                            {/* Graph Section */}
                            <HierarchicalTreeChart
                                data={decompositionGraphData}
                                height={chartHeight}
                                width={chartWidth - 50}
                                containerId="decomposition-container-legacy"
                                onNodeClick={() => {}}
                                loading={isDecompositionGraphDataLoading}
                            />
                        </>
                    ) : (
                        <div className="flex items-center justify-center text-gray-500" style={{ height: chartHeight }}>
                            <p>Select a bar from the chart above to view decomposition</p>
                        </div>
                    )}
                </>
            )}
        </>
    );
};

export default memo(DecompositionGraph);
